import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../api/api";
import { assignBasketSerials, formatSerialRanges } from "../utils/inventorySerials";
import "./css/InventoryPage.css";
import "./css/InvoicesPage.css";
import TopNav from "../components/TopNav";
import ReceivingInvoiceDocument from "../components/ReceivingInvoiceDocument";
import InvoicePrintEmailActions from "../components/InvoicePrintEmailActions";
import EmailActions from "../components/EmailActions";
import ConfirmModal from "../components/ConfirmModal";
import AlertModal from "../components/AlertModal";
import { useAuth } from "../context/AuthContext";
import { can } from "../accessControl";
import {
  formatDateTimeConsistent,
} from "../utils/dateDisplay";

const LOCAL_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;


const formatLocalTimestampText = (value) => {
  if (!value) return "";
  const match = String(value).trim().match(LOCAL_TIMESTAMP_PATTERN);
  if (!match) return "";
  const [, year, month, day, hourText, minute, secondText = "00"] = match;
  const hour = Number(hourText);
  const second = Number(secondText);
  if (!Number.isFinite(hour) || !Number.isFinite(second)) return "";
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  const secondDisplay = second ? `:${String(second).padStart(2, "0")}` : "";
  return `${Number(month)}/${Number(day)}/${year}, ${hour12}:${minute}${secondDisplay} ${suffix}`;
};

const transactionTimestampDisplay = (value) => {
  const formatted = formatLocalTimestampText(value) || formatDateTimeConsistent(value, {
    timeZone: "America/New_York",
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  if (!formatted) return { primary: "-", secondary: "" };
  const [primary, ...secondaryParts] = formatted.split(", ");
  return {
    primary,
    secondary: secondaryParts.join(", "),
  };
};

const renderTransactionTimestamp = (value) => {
  const display = transactionTimestampDisplay(value);
  return (
    <>
      {display.primary}
      {display.secondary && <small className="transfer-date-note">{display.secondary}</small>}
    </>
  );
};

const normalizeSearchText = (value) => String(value || "")
  .trim()
  .replace(/\s+/g, " ")
  .toLowerCase();

const INACTIVE_RECEIVING_LOCATION_MESSAGE = "Receiving Inventory is not allowed to Inactive Locations.";

function InventoryTypeaheadSelect({ value, onChange, placeholder, options, onSelect, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const query = normalizeSearchText(value);
  const filteredOptions = options
    .filter((option) => !query || normalizeSearchText(option.label).includes(query))
    .slice(0, 60);
  const menuId = `inventory-typeahead-${placeholder.replace(/\s+/g, '-').toLowerCase()}`;

  const chooseOption = (option) => {
    if (disabled) return;
    onChange(option.label);
    onSelect?.(option);
    setIsOpen(false);
    setActiveIndex(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      if (disabled) return;
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) => (
        filteredOptions.length ? Math.min(current + 1, filteredOptions.length - 1) : 0
      ));
      return;
    }

    if (e.key === "ArrowUp") {
      if (disabled) return;
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setActiveIndex(0);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (disabled) return;
      if (isOpen && filteredOptions[activeIndex]) {
        chooseOption(filteredOptions[activeIndex]);
      }
      return;
    }

    if (e.key === "Tab" && isOpen && filteredOptions[activeIndex]) {
      if (disabled) return;
      chooseOption(filteredOptions[activeIndex]);
    }
  };

  return (
    <div className="inventory-typeahead">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          if (disabled) return;
          onChange(e.target.value);
          setIsOpen(true);
          setActiveIndex(0);
        }}
        onFocus={() => {
          if (disabled) return;
          setIsOpen(true);
          setActiveIndex(0);
        }}
        onBlur={() => setTimeout(() => setIsOpen(false), 120)}
        onKeyDown={handleKeyDown}
        className="inventory-select-input"
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={menuId}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div className="inventory-typeahead-menu" id={menuId} role="listbox">
          {filteredOptions.map((option, index) => (
            <button
              type="button"
              key={`${option.value}-${index}`}
              className={index === activeIndex ? "active" : ""}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                chooseOption(option);
              }}
              role="option"
              aria-selected={index === activeIndex}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InventoryPage() {
  const { user } = useAuth();
  const canCreateInventory = can(user, "inventory.create");
  const canEditInventory = can(user, "inventory.edit");
  const canDeleteInventory = can(user, "inventory.delete");
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsLoadError, setItemsLoadError] = useState(false);
  const [highlighted, setHighlighted] = useState(new Set());
  const [newItemIds, setNewItemIds] = useState(() => new Set());
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [locations, setLocations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [orderBasket, setOrderBasket] = useState([]);
  const [isOrderOverlayOpen, setIsOrderOverlayOpen] = useState(false);
  const [orderLocationId, setOrderLocationId] = useState("");
  const [orderLocationQuery, setOrderLocationQuery] = useState("");
  const [orderVendorId, setOrderVendorId] = useState("");
  const [orderVendorQuery, setOrderVendorQuery] = useState("");
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [manualSerialDialog, setManualSerialDialog] = useState(null);
  const serialStateRef = useRef(null);
  const orderBusyRef = useRef(false);
  const skipOrderUnloadPromptRef = useRef(false);
  const [createdOrderInvoice, setCreatedOrderInvoice] = useState(null);
  const [createdOrderInvoiceLoading, setCreatedOrderInvoiceLoading] = useState(false);
  const [createdOrderInvoiceError, setCreatedOrderInvoiceError] = useState("");
  const [createdOrderEmailLoading, setCreatedOrderEmailLoading] = useState(false);
  const [stockModal, setStockModal] = useState({
    open: false,
    loading: false,
    error: "",
    data: null,
  });
  const [stockDateSort, setStockDateSort] = useState("asc");
  const [stockEmailLoading, setStockEmailLoading] = useState(false);

  // -----------------------------
  // FILTER STATE
  // -----------------------------
  const [filters, setFilters] = useState({
    length: new Set(),
    color: new Set(),
    cap_size: new Set(),
    collection: new Set(),
  });

  const [showInactive, setShowInactive] = useState(false);

  const [openFilter, setOpenFilter] = useState(null);

  const [showZeroQty, setShowZeroQty] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  // -----------------------------
  // MODALS
  // -----------------------------
  const [alertState, setAlertState] = useState({ open: false, message: "" });
  const [confirmState, setConfirmState] = useState({
    open: false,
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    document.title = "Inventory - Services Irene's Wigs.com";
  }, []);

  const showAlert = (msg) =>
    setAlertState({ open: true, message: msg });

  const askConfirm = (message, onConfirm) => {
    setConfirmState({
      open: true,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmState({ open: false, message: "", onConfirm: () => {} });
      },
    });
  };

  // -----------------------------
  // FETCH ITEMS
  // -----------------------------
  const fetchItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const res = await api.get("/inventory");
      setItems(res.data);
      setItemsLoadError(false);
    } catch (err) {
      console.error("Failed to fetch items:", err);
      setItemsLoadError(true);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const [locationsRes, vendorsRes] = await Promise.all([
          api.get("/locations"),
          api.get("/vendors"),
        ]);
        setLocations(locationsRes.data || []);
        setVendors(vendorsRes.data || []);
      } catch (err) {
        console.error("Failed to fetch order options:", err);
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    if (orderBasket.length === 0) return undefined;

    const handleBeforeUnload = (event) => {
      if (skipOrderUnloadPromptRef.current) return undefined;
      event.preventDefault();
      event.returnValue = "Current order will be lost.";
      return event.returnValue;
    };

    const handlePageLinkClick = (event) => {
      const link = event.target.closest?.("a");
      if (!link || !link.href) return;
      if (link.target && link.target !== "_self") return;
      if (link.origin !== window.location.origin) return;

      event.preventDefault();
      askConfirm("Current order will be lost. Leave this page?", () => {
        skipOrderUnloadPromptRef.current = true;
        setOrderBasket([]);
        window.location.assign(link.href);
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handlePageLinkClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handlePageLinkClick, true);
    };
  }, [orderBasket.length]);

  // -----------------------------
  // FILTER APPLICATION
  // -----------------------------
const filteredItems = items.filter((item) => {
  if (!showInactive && (item.is_active === false || item.is_active === 0)) {
    return false;
  }

  if (!showZeroQty && Number(item.quantity_on_hand) <= 0 && !newItemIds.has(item.id)) {
    return false;
  }

  for (const key of Object.keys(filters)) {
    const selected = filters[key];
    if (selected.size > 0 && !selected.has(item[key])) {
      return false;
    }
  }

  const q = normalizeSearchText(searchTerm);
  if (q) {
    const matchesSearch =
  normalizeSearchText(item.name).includes(q) ||
  normalizeSearchText(item.full_name).includes(q); // ||
  //String(item.nc_id || "").toLowerCase().includes(q) ||
  //String(item.list_id || "").toLowerCase().includes(q) ||
  //String(item.upc || "").toLowerCase().includes(q);

    if (!matchesSearch) {
      return false;
    }
  }

  return true;
});

  const activeOrderLocations = locations.filter((location) => location.active !== false);
  const inactiveOrderLocations = locations.filter((location) => location.active === false);
  const findOrderLocationByQuery = (locationList, query) => {
    const text = String(query || "").trim();
    if (!text) return null;
    return locationList.find((loc) => String(loc.id) === text)
      || locationList.find((loc) => String(loc.name || "").trim().toLowerCase() === text.toLowerCase())
      || null;
  };
  const orderLocationOptions = activeOrderLocations.map((location) => ({
    value: location.id,
    label: location.name,
  })).filter((option) => option.label);
  const orderVendorOptions = vendors.map((vendor) => ({
    value: vendor.id,
    label: vendor.name || vendor.company_name,
  })).filter((option) => option.label);
  const orderBasketCount = orderBasket.reduce(
    (total, item) => total + (Number(item.orderQty) || 1),
    0
  );



  // -----------------------------
  // PAGINATION
  // -----------------------------
  //const PAGE_SIZE = 100000;
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE);

  const paginatedItems = filteredItems.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  useEffect(() => {
  setPage(1);
}, [filters, showInactive, showZeroQty, searchTerm]);

  // -----------------------------
  // FILTER OPTIONS (from paginated results)
  // -----------------------------
  const filterOptions = {
  length: [...new Set(filteredItems.map(i => i.length).filter(Boolean))].sort(),
  color: [...new Set(filteredItems.map(i => i.color).filter(Boolean))].sort(),
  cap_size: [...new Set(filteredItems.map(i => i.cap_size).filter(Boolean))].sort(),
  collection: [...new Set(filteredItems.map(i => i.collection).filter(Boolean))].sort(),
};

  // -----------------------------
  // INLINE EDITING
  // -----------------------------
  const handleInventoryChange = (id, field, value) => {
    if (!canEditInventory) return;
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleUpdate = async (id) => {
    if (!canEditInventory) {
      showAlert("You have read-only inventory access.");
      return;
    }
    const item = items.find((i) => i.id === id);

    try {
      await api.put(`/inventory/${id}`, item);

      setHighlighted((prev) => new Set(prev.add(id)));

      setTimeout(() => {
        setHighlighted((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }, 1500);

      fetchItems();
    } catch (err) {
      console.error("Error updating item:", err);
    }
  };

  const handleNewItem = async () => {
    if (!canCreateInventory) {
      showAlert("You have read-only inventory access.");
      return;
    }
    try {
      const res = await api.post("/inventory", {
        name: "New Item",
        auto_increment_list_id: true,
        quantity_on_hand: 0,
        has_lot_serial: 1,
        is_active: true,
      });
      const newId = res.data?.id;

      if (newId) {
        setNewItemIds((prev) => new Set([...prev, newId]));
        setHighlighted((prev) => new Set(prev.add(newId)));
      }

      setSearchTerm("");
      setFilters({
        length: new Set(),
        color: new Set(),
        cap_size: new Set(),
        collection: new Set(),
      });
      setShowInactive(false);
      setPage(1);
      await fetchItems();
      showAlert(`New item added${res.data?.list_id ? ` with List ID ${res.data.list_id}` : ""}.`);
    } catch (err) {
      console.error("Error adding new item:", err);
      showAlert(err.response?.data?.error || "Failed to add new item.");
    }
  };

  // -----------------------------
  // BULK SELECT
  // -----------------------------
  const handleCheckboxChange = (id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // -----------------------------
  // BULK DELETE
  // -----------------------------
  const handleDeleteSelected = () => {
    if (!canDeleteInventory) {
      showAlert("You do not have permission to delete inventory.");
      return;
    }
    if (selectedItems.size === 0) {
      showAlert("Select an item to delete.");
      return;
    }

    const count = selectedItems.size;
    askConfirm(`Delete ${count === 1 ? "selected item" : `${count} selected items`}?`, async () => {
      try {
        await Promise.all(
          [...selectedItems].map((id) => api.delete(`/inventory/${id}`))
        );
        setSelectedItems(new Set());
        showAlert(count === 1 ? "Selected item deleted." : "Selected items deleted.");
        fetchItems();
      } catch (err) {
        console.error(err);
        showAlert(err.response?.data?.error || err.response?.data?.message || "Failed to delete selected item.");
      }
    });
  };

  const openManualSerialDialog = (item) => {
    if (!canCreateInventory || orderBusyRef.current) return;
    const existing = orderBasket.find(row => row.id === item.id);
    setManualSerialDialog({ item, input: existing?.usesCustomSerials ? existing.serialNumbers.join('\n') : '', error: '' });
  };

  const saveManualSerials = async () => {
    if (!manualSerialDialog || !canCreateInventory || orderBusyRef.current) return;
    const serialNumbers = manualSerialDialog.input.split(/[\n,;\t]+/).map(value => value.trim()).filter(Boolean);
    const keys = serialNumbers.map(value => value.toLowerCase());
    const otherManual = new Set(orderBasket.filter(row => row.usesCustomSerials && row.id !== manualSerialDialog.item.id)
      .flatMap(row => row.serialNumbers).map(value => value.toLowerCase()));
    if (!serialNumbers.length || serialNumbers.length > 10000 || new Set(keys).size !== keys.length || keys.some(key => otherManual.has(key))) {
      setManualSerialDialog(prev => ({ ...prev, error: 'Enter 1–10,000 unique serial numbers. A serial cannot appear twice in this order.' }));
      return;
    }
    orderBusyRef.current = true;
    setOrderSubmitting(true);
    try {
      const { data } = await api.get('/inventory/serial-sequence');
      serialStateRef.current = data;
      const manualItem = { ...manualSerialDialog.item, usesCustomSerials: true, serialNumbers, orderQty: serialNumbers.length };
      setOrderBasket(prev => assignBasketSerials(prev.some(row => row.id === manualItem.id)
        ? prev.map(row => row.id === manualItem.id ? manualItem : row)
        : [...prev, manualItem], data));
      setManualSerialDialog(null);
      setIsOrderOverlayOpen(true);
    } catch (err) {
      setManualSerialDialog(prev => ({ ...prev, error: err.response?.data?.error || 'Failed to prepare the order.' }));
    } finally {
      orderBusyRef.current = false;
      setOrderSubmitting(false);
    }
  };

  const addItemsToOrder = async (rows) => {
    if (!canCreateInventory || orderBusyRef.current) return;
    orderBusyRef.current = true;
    setOrderSubmitting(true);
    try {
      const { data } = await api.get('/inventory/serial-sequence');
      serialStateRef.current = data;
      setOrderBasket(prev => {
        const existingIds = new Set(prev.map(item => item.id));
        const additions = rows.filter(item => !existingIds.has(item.id))
          .map(item => ({ ...item, orderQty: 1 }));
        return assignBasketSerials([...prev, ...additions], data);
      });
      setSelectedItems(new Set());
      setIsOrderOverlayOpen(true);
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to prepare serial numbers.');
    } finally {
      orderBusyRef.current = false;
      setOrderSubmitting(false);
    }
  };

  const handleCreateOrAddToOrder = () => {
    if (selectedItems.size === 0) {
      showAlert('Select one or more items to add to the order.');
      return;
    }
    return addItemsToOrder(items.filter(item => selectedItems.has(item.id)));
  };

  const openStockForItem = async (id) => {
    setStockDateSort("asc");
    setStockModal({ open: true, loading: true, error: "", data: null });

    try {
      const res = await api.get(`/inventory/${id}/stock`);
      setStockModal({ open: true, loading: false, error: "", data: res.data });
    } catch (err) {
      console.error("Failed to check stock:", err);
      setStockModal({
        open: true,
        loading: false,
        error: err.response?.data?.error || "Failed to check stock.",
        data: null,
      });
    }
  };

  const handleCheckStock = async () => {
    if (selectedItems.size !== 1) {
      showAlert("Select one item to check stock.");
      return;
    }

    const [id] = Array.from(selectedItems);
    await openStockForItem(id);
  };

  const closeStockModal = () => {
    setStockModal({ open: false, loading: false, error: "", data: null });
    setStockEmailLoading(false);
  };

  const handleStockBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      closeStockModal();
    }
  };

  const stopStockOverlayEvent = (event) => {
    event.stopPropagation();
  };

  const handleStockCloseClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeStockModal();
  };

  const printStockCheck = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    document.body.classList.add("inventory-stock-printing");
    window.setTimeout(() => {
      window.print();
      window.setTimeout(() => {
        document.body.classList.remove("inventory-stock-printing");
      }, 250);
    }, 50);
  };

  const removeOrderBasketItem = (id) => {
    if (!canCreateInventory) return;
    if (orderBusyRef.current) return;
    setOrderBasket((prev) => assignBasketSerials(prev.filter((item) => item.id !== id), serialStateRef.current));
  };

  const cancelOrder = () => {
    if (!canCreateInventory) return;
    askConfirm("Cancel current order?", () => {
      setOrderBasket([]);
      setOrderLocationId("");
      setOrderLocationQuery("");
      setOrderVendorId("");
      setOrderVendorQuery("");
      setIsOrderOverlayOpen(false);
    });
  };

  const updateOrderItemQuantity = (id, nextQty) => {
    if (!canCreateInventory || orderBusyRef.current) return;
    const quantity = Math.min(10000, Math.max(1, Math.floor(Number(nextQty) || 1)));
    setOrderBasket(prev => assignBasketSerials(prev.map(item => (
      item.id === id ? { ...item, orderQty: quantity } : item
    )), serialStateRef.current));
  };

  const openCreatedOrderInvoice = async (docNo) => {
    if (!docNo) {
      setCreatedOrderInvoice(null);
      setCreatedOrderInvoiceError("Shipdoc was created, but no doc number was returned.");
      return;
    }

    setCreatedOrderInvoice(null);
    setCreatedOrderInvoiceError("");
    setCreatedOrderInvoiceLoading(true);

    try {
      const res = await api.get(`/invoices/${docNo}`, { params: { transactionTypeId: 2 } });
      setCreatedOrderInvoice(res.data);
    } catch (err) {
      console.error("Failed to load created order invoice:", err);
      setCreatedOrderInvoiceError(err.response?.data?.error || "Failed to load created order invoice.");
    } finally {
      setCreatedOrderInvoiceLoading(false);
    }
  };

  const closeCreatedOrderInvoice = () => {
    setCreatedOrderInvoice(null);
    setCreatedOrderInvoiceLoading(false);
    setCreatedOrderInvoiceError("");
  };

  const completeOrder = async () => {
    if (orderBusyRef.current) return;
    if (!canCreateInventory) {
      showAlert("You have read-only inventory access.");
      return;
    }
    if (orderBasket.length === 0) {
      showAlert("Add items to the order before completing it.");
      return;
    }

    const location = findOrderLocationByQuery(activeOrderLocations, orderLocationId)
      || findOrderLocationByQuery(activeOrderLocations, orderLocationQuery);
    const inactiveLocation = findOrderLocationByQuery(inactiveOrderLocations, orderLocationId)
      || findOrderLocationByQuery(inactiveOrderLocations, orderLocationQuery);
    if (inactiveLocation) {
      showAlert(INACTIVE_RECEIVING_LOCATION_MESSAGE);
      return;
    }
    if (!location) {
      showAlert("Select a location.");
      return;
    }
    const vendor = vendors.find((item) => String(item.id) === String(orderVendorId))
      || vendors.find((item) => (item.name || item.company_name || "").toLowerCase() === orderVendorQuery.trim().toLowerCase());
    if (!vendor) {
      showAlert("Select a vendor.");
      return;
    }

    orderBusyRef.current = true;
    setOrderSubmitting(true);

    try {
      const shipDocRes = await api.post("/inventory/shipdoc", {
        locationId: location.id,
        vendorId: vendor.id,
        items: orderBasket.map((item) => ({
          inventory_id: item.nc_id || item.id,
          quantity: item.orderQty,
          serialNumbers: item.serialNumbers || [],
          usesCustomSerials: item.usesCustomSerials === true,
        })),
      });

      setOrderBasket([]);
      setOrderLocationId("");
      setOrderLocationQuery("");
      setOrderVendorId("");
      setOrderVendorQuery("");
      setIsOrderOverlayOpen(false);
      await fetchItems();
      await openCreatedOrderInvoice(shipDocRes.data?.docNo);
    } catch (err) {
      console.error("Failed to complete order:", err);
      if (err.response?.data?.code === 'SERIAL_PREVIEW_CHANGED') {
        serialStateRef.current = err.response.data.serialState;
        setOrderBasket(prev => assignBasketSerials(prev, serialStateRef.current));
      }
      showAlert(err.response?.data?.error || "Failed to complete order.");
    } finally {
      orderBusyRef.current = false;
      setOrderSubmitting(false);
    }
  };

  // -----------------------------
  // FILTER DROPDOWN COMPONENT
  // -----------------------------
  const ColumnFilter = ({ column }) => {
    const options = filterOptions[column];
    const selected = filters[column];

    const toggleValue = (value) => {
      setFilters((prev) => {
        const updated = new Set(prev[column]);
        updated.has(value) ? updated.delete(value) : updated.add(value);
        return { ...prev, [column]: updated };
      });
    };

    const selectAll = () => {
      setFilters((prev) => ({
        ...prev,
        [column]: new Set(options),
      }));
    };

    const clearAll = () => {
      setFilters((prev) => ({
        ...prev,
        [column]: new Set(),
      }));
    };

    return (
      <div
        className="filter-dropdown"
        onClick={(event) => event.stopPropagation()}
        onMouseLeave={() => setOpenFilter(null)}
      >
        <div className="filter-actions">
          <button onClick={selectAll}>Select All</button>
          <button onClick={clearAll}>Clear</button>
        </div>

        <div className="filter-options">
          {options.map((value) => (
            <label key={value} className="filter-option">
              <input
                type="checkbox"
                checked={selected.has(value)}
                onChange={() => toggleValue(value)}
              />
              <span className="filter-value">{value}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  // -----------------------------
  // RENDER
  // -----------------------------
  const sortedStockRows = [...(stockModal.data?.rows || [])].sort((left, right) => {
    if (!left.transferDate && !right.transferDate) return 0;
    if (!left.transferDate) return 1;
    if (!right.transferDate) return -1;
    const leftTime = left.transferDate ? new Date(left.transferDate).getTime() : 0;
    const rightTime = right.transferDate ? new Date(right.transferDate).getTime() : 0;
    return stockDateSort === "asc" ? leftTime - rightTime : rightTime - leftTime;
  });

  return (
    <>
      <TopNav />

      <div className="inventory-table-wrapper">
        <div className="table-toggles">
  <label className="toggle-control">
    <input
      type="checkbox"
      checked={showInactive}
      onChange={() => setShowInactive(prev => !prev)}
    />
    <span>Show inactive items</span>
  </label>

  <label className="toggle-control">
    <input
      type="checkbox"
      checked={showZeroQty}
      onChange={() => setShowZeroQty(prev => !prev)}
    />
    <span>Show zero-quantity items</span>
  </label>
</div>

<div className="inventory-toolbar">
  <input
    type="text"
    className="inventory-search"
    placeholder="Search by Name/Number"
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
  />
</div>

<div className="results-count">
  {itemsLoading ? 'Loading inventory…' : itemsLoadError ? (
    <span role="alert">
      Unable to load current inventory quantities.{' '}
      {items.length > 0 && 'Displayed quantities may be outdated. '}
      <button type="button" onClick={fetchItems}>Retry</button>
    </span>
  ) : filteredItems.length === items.length
    ? `Total items: ${items.length}`
    : `Showing ${filteredItems.length} of ${items.length} items`}
</div>


        <table className="inventory-table">
          <thead>
            <tr>
              {/* PAGINATION HEADER */}
              <th className="pagination-header">
                <div className="inventory-header-actions">
                  <button
                    type="button"
                    className="new-item-btn"
                    onClick={handleNewItem}
                    disabled={!canCreateInventory || orderSubmitting}
                  >
                    New
                  </button>
                  <button
                    type="button"
                    className="delete-selected-btn"
                    onClick={handleDeleteSelected}
                    disabled={!canDeleteInventory || selectedItems.size === 0}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="check-stock-btn"
                    onClick={handleCheckStock}
                    disabled={selectedItems.size !== 1}
                  >
                    Check Stock
                  </button>
                  <button
                    type="button"
                    className="create-order-btn"
                    onClick={handleCreateOrAddToOrder}
                    disabled={!canCreateInventory || orderSubmitting}
                  >
                    {orderBasket.length > 0 ? "Add to Order" : "Create Order"}
                  </button>
                  <button
                    type="button"
                    className="inventory-basket-btn"
                    onClick={() => setIsOrderOverlayOpen(true)}
                    disabled={!canCreateInventory || orderBasket.length === 0}
                    title={orderBasket.length > 0 ? "Open current order" : "Basket is empty"}
                    aria-label={orderBasket.length > 0 ? `Open current order with ${orderBasketCount} item${orderBasketCount === 1 ? "" : "s"}` : "Basket is empty"}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M7 10l2.5-5h5L17 10" />
                      <path d="M4.5 10h15l-1.3 9H5.8L4.5 10z" />
                      <path d="M8 13v3M12 13v3M16 13v3" />
                    </svg>
                    {orderBasket.length > 0 && (
                      <span className="inventory-basket-count">{orderBasketCount}</span>
                    )}
                  </button>
                </div>
                {filteredItems.length > PAGE_SIZE && (
                  <div className="pager">
                    <button
                      className="pager-btn"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      ❮
                    </button>

                    <div className="pager-label">
                      {page} of {totalPages}
                    </div>

                    <button
                      className="pager-btn"
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      ❯
                    </button>
                  </div>
                )}
              </th>
              <th className="inventory-name-column">Name</th>
              <th>Price</th>
              <th>Qty</th>

              {/* FILTER HEADERS */}
              {["length", "color", "cap_size", "collection"].map((col) => (
                <th
                  key={col}
                  className={`filter-header ${openFilter === col ? "filter-open" : ""}`}
                  onClick={() => setOpenFilter(openFilter === col ? null : col)}
                  onKeyDown={(event) => {
                    if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                      event.preventDefault();
                      setOpenFilter(openFilter === col ? null : col);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="inventory-filter-trigger">
                    <span>{col.replace("_", " ").toUpperCase()}</span>
                    <button type="button" className="filter-icon-button" aria-label={`Filter ${col.replace("_", " ")}`}>
                      <svg viewBox="0 0 24 24" aria-hidden="true" className="filter-icon">
                        <path d="M4 5h16l-6.5 7.5V18l-3 1v-6.5L4 5z" />
                      </svg>
                    </button>
                    {openFilter === col && <ColumnFilter column={col} />}
                  </div>
                </th>
              ))}

              <th>UPC</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginatedItems.map((item) => (
              <tr
                key={item.id}
                className={highlighted.has(item.id) ? "highlight-row" : ""}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedItems.has(item.id)}
                    onChange={() => handleCheckboxChange(item.id)}
                  />
                </td>
                <td className="inventory-name-column">
                  <input
                    value={item.name || ""}
                    disabled={!canEditInventory}
                    onChange={(e) =>
                      handleInventoryChange(item.id, "name", e.target.value)
                    }
                  />
                </td>

                <td>
                  <input
                    type="number"
                    step="0.01"
                    className="inventory-price-input"
                    onKeyDown={(event) => {
                      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                        event.preventDefault();
                      }
                    }}
                    value={item.price || ""}
                    disabled={!canEditInventory}
                    onChange={(e) =>
                      handleInventoryChange(item.id, "price", Number(e.target.value))
                    }
                  />
                </td>

                <td className="inventory-qty-column">
                  <button
                    type="button"
                    className="inventory-qty-link"
                    onClick={() => openStockForItem(item.id)}
                  >
                    {item.quantity_on_hand ?? ""}
                  </button>
                </td>

                <td className="inventory-length-column">
                  <input
                    value={item.length || ""}
                    disabled={!canEditInventory}
                    onChange={(e) =>
                      handleInventoryChange(item.id, "length", e.target.value)
                    }
                  />
                </td>

                <td className="inventory-color-column">
                  <input
                    value={item.color || ""}
                    disabled={!canEditInventory}
                    onChange={(e) =>
                      handleInventoryChange(item.id, "color", e.target.value)
                    }
                  />
                </td>

                <td className="inventory-cap-size-column">
                  <input
                    value={item.cap_size || ""}
                    disabled={!canEditInventory}
                    onChange={(e) =>
                      handleInventoryChange(item.id, "cap_size", e.target.value)
                    }
                  />
                </td>

                <td className="inventory-collection-column">
                  <input
                    value={item.collection || ""}
                    disabled={!canEditInventory}
                    onChange={(e) =>
                      handleInventoryChange(item.id, "collection", e.target.value)
                    }
                  />
                </td>

                <td>
                  <input
                    value={item.upc || ""}
                    disabled={!canEditInventory}
                    onChange={(e) =>
                      handleInventoryChange(item.id, "upc", e.target.value)
                    }
                  />
                </td>

                <td>
                  <div className="inventory-row-actions">
                    <button
                      className="update-btn"
                      onClick={() => handleUpdate(item.id)}
                      disabled={!canEditInventory}
                    >
                      Update
                    </button>
                    <button
                      type="button"
                      className="add-new-sn-btn"
                      onClick={() => openManualSerialDialog(item)}
                      disabled={!canCreateInventory || orderSubmitting}
                    >
                      Add New SN
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODALS */}
      {alertState.open && (
        <AlertModal
          message={alertState.message}
          onClose={() => setAlertState({ open: false, message: "" })}
        />
      )}

      {confirmState.open && (
        <ConfirmModal
          open={confirmState.open}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() =>
            setConfirmState({ open: false, message: "", onConfirm: () => {} })
          }
        />
      )}

      {manualSerialDialog && (
        <div className="inventory-custom-sn-overlay">
          <div className="inventory-custom-sn-modal" role="dialog" aria-modal="true" aria-label="Add New SN">
            <div className="inventory-custom-sn-header"><h3>Add New SN</h3>
              <button type="button" className="inventory-secondary-btn" onClick={() => setManualSerialDialog(null)} disabled={orderSubmitting}>Close</button>
            </div>
            <div className="inventory-custom-sn-item"><strong>{manualSerialDialog.item.name}</strong></div>
            <label className="inventory-custom-sn-field">Serial Numbers
              <textarea autoFocus value={manualSerialDialog.input} disabled={orderSubmitting}
                placeholder="Scan or enter serial numbers, one per line"
                onChange={event => setManualSerialDialog(prev => ({ ...prev, input: event.target.value, error: '' }))} />
            </label>
            <p>These serials replace this item's current basket serials. Quantity is set from the number entered.</p>
            {manualSerialDialog.error && <div className="inventory-custom-sn-error">{manualSerialDialog.error}</div>}
            <div className="inventory-custom-sn-actions">
              <button type="button" className="cancel-order-btn" onClick={() => setManualSerialDialog(null)} disabled={orderSubmitting}>Cancel</button>
              <button type="button" className="complete-order-btn" onClick={saveManualSerials} disabled={orderSubmitting}>Add to Order</button>
            </div>
          </div>
        </div>
      )}

      {isOrderOverlayOpen && orderBasket.length > 0 && (
        <div className="inventory-order-overlay">
          <div className="inventory-order-panel">
            <div className="inventory-order-header">
              <h3>Current Order</h3>
              <button
                type="button"
                className="inventory-order-close"
                onClick={() => setIsOrderOverlayOpen(false)}
              >
                x
              </button>
            </div>

            <div className="inventory-order-select-grid">
              <label className="inventory-order-location">
                Vendor
                <InventoryTypeaheadSelect
                  placeholder="Vendor"
                  value={orderVendorQuery}
                  onChange={(value) => {
                    const matchedVendor = vendors.find(
                      (vendor) => (vendor.name || vendor.company_name || "").toLowerCase() === value.trim().toLowerCase()
                    );
                    setOrderVendorQuery(value);
                    setOrderVendorId(matchedVendor ? String(matchedVendor.id) : "");
                  }}
                  onSelect={(option) => setOrderVendorId(String(option.value))}
                  options={orderVendorOptions}
                  disabled={!canCreateInventory || orderSubmitting}
                />
              </label>

              <label className="inventory-order-location">
                Location
                <InventoryTypeaheadSelect
                  placeholder="Receiving Location"
                  value={orderLocationQuery}
                  onChange={(value) => {
                    const matchedLocation = findOrderLocationByQuery(activeOrderLocations, value);
                    setOrderLocationQuery(value);
                    setOrderLocationId(matchedLocation ? String(matchedLocation.id) : "");
                  }}
                  onSelect={(option) => setOrderLocationId(String(option.value))}
                  options={orderLocationOptions}
                  disabled={!canCreateInventory || orderSubmitting}
                />
              </label>
            </div>

            <div className="inventory-order-items">
              <p>Serials are assigned when receiving is saved. Any change to the range will be shown for review.</p>
              {orderBasket.map((item) => (
                <div className="inventory-order-item" key={item.id}>
                  <div>
                    <strong>{item.name || item.full_name || "Unnamed item"}</strong>
                    <span>Serials: {formatSerialRanges(item.serialNumbers)}</span>
                    <span>NC {item.nc_id || item.id} · List {item.list_id || ""}</span>
                  </div>
                  {item.usesCustomSerials && <button type="button" className="inventory-edit-serials-btn" onClick={() => openManualSerialDialog(item)} disabled={orderSubmitting}>Edit Serials</button>}
                  <label className="inventory-order-qty">
                    Qty
                    <input
                      type="number"
                      min="1"
                      value={item.orderQty || 1}
                      disabled={!canCreateInventory || orderSubmitting || item.usesCustomSerials}
                      onChange={(e) => updateOrderItemQuantity(item.id, e.target.value)}
                    />
                  </label>
                  <button type="button" className="inventory-remove-serials-btn" onClick={() => removeOrderBasketItem(item.id)} disabled={!canCreateInventory || orderSubmitting}>
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="inventory-order-footer">
              <button
                type="button"
                className="cancel-order-btn"
                onClick={cancelOrder}
                disabled={!canCreateInventory || orderSubmitting}
              >
                Cancel Order
              </button>
              <button
                type="button"
                className="complete-order-btn"
                onClick={completeOrder}
                disabled={!canCreateInventory || orderSubmitting}
              >
                {orderSubmitting ? "Completing..." : "Complete Order"}
              </button>
            </div>
          </div>
        </div>
      )}

      {(createdOrderInvoice || createdOrderInvoiceLoading || createdOrderInvoiceError) && (
        <div className="invoice-modal-overlay print-modal-overlay" onClick={closeCreatedOrderInvoice}>
          <div className="invoice-modal print-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-modal-header">
              <h3>
                {createdOrderInvoice
                  ? `Invoice #${createdOrderInvoice.invoiceNo}`
                  : "Invoice"}
              </h3>
              <div className="invoice-modal-actions inventory-invoice-modal-actions">
                <InvoicePrintEmailActions
                  invoice={createdOrderInvoice}
                  disabled={!createdOrderInvoice}
                  loading={createdOrderEmailLoading}
                  onLoadingChange={setCreatedOrderEmailLoading}
                  successMessage={(to) => `Invoice #${createdOrderInvoice.invoiceNo} emailed to ${to}.`}
                  onError={showAlert}
                />
                <button
                  type="button"
                  className="button small red"
                  onClick={closeCreatedOrderInvoice}
                >
                  Close
                </button>
              </div>
            </div>
            {createdOrderInvoiceLoading && <p className="invoice-modal-status">Loading invoice...</p>}
            {!createdOrderInvoiceLoading && createdOrderInvoiceError && (
              <p className="invoice-modal-status error">{createdOrderInvoiceError}</p>
            )}
            {!createdOrderInvoiceLoading && createdOrderInvoice && (
              <ReceivingInvoiceDocument invoice={createdOrderInvoice} />
            )}
          </div>
        </div>
      )}

      {stockModal.open && (
        <div
          className="inventory-stock-overlay"
          onClick={handleStockBackdropClick}
        >
          <div
            className="inventory-stock-modal"
            onClick={stopStockOverlayEvent}
            onMouseDown={stopStockOverlayEvent}
            onPointerDown={stopStockOverlayEvent}
          >
            <div className="inventory-stock-header">
              <h3>Stock Check</h3>
              <div className="inventory-stock-actions">
                {!stockModal.loading && stockModal.data && (
                  <>
                    <button
                      type="button"
                      className="button small blue"
                      onClick={printStockCheck}
                      onMouseDown={stopStockOverlayEvent}
                      onPointerDown={stopStockOverlayEvent}
                    >
                      Print
                    </button>
                    <EmailActions
                      endpoint={`/inventory/${stockModal.data.item?.id}/stock/email`}
                      templateKey="stockCheck"
                      templateValues={{
                        document: 'Stock Check',
                        documentNumber: '',
                        itemName: stockModal.data.item?.name || stockModal.data.item?.full_name || stockModal.data.item?.id || '',
                      }}
                      subject={`Stock Check - ${stockModal.data.item?.name || stockModal.data.item?.full_name || stockModal.data.item?.id || ''}`}
                      quickTitle="Send Stock Check"
                      quickPrompt="Send stock check to:"
                      attachmentPdf={`Stock_Check_${stockModal.data.item?.id || 'item'}.pdf`}
                      attachmentExcel={`Stock_Check_${stockModal.data.item?.id || 'item'}.xls`}
                      loading={stockEmailLoading}
                      onLoadingChange={setStockEmailLoading}
                      successMessage={(to) => `Stock check emailed to ${to}.`}
                      onError={showAlert}
                    />
                  </>
                )}
                <button
                  type="button"
                  className="button small red"
                  onClick={handleStockCloseClick}
                >
                  Close
                </button>
              </div>
            </div>
            {stockModal.loading && <p>Loading stock...</p>}
            {!stockModal.loading && stockModal.error && <p className="inventory-stock-error">{stockModal.error}</p>}
            {!stockModal.loading && stockModal.data && (
              <>
                <div className="inventory-stock-summary">
                  <strong>{stockModal.data.item?.name || stockModal.data.item?.full_name || "Inventory item"}</strong>
                  <span>NC {stockModal.data.item?.id} - List {stockModal.data.item?.list_id || ""}</span>
                  <span
                    className={
                      (stockModal.data.rows || []).some((row) => Number(row.quantityBalance) > 0)
                        ? "inventory-stock-available"
                        : "inventory-stock-unavailable"
                    }
                  >
                    {(stockModal.data.rows || []).some((row) => Number(row.quantityBalance) > 0)
                      ? `Available: Yes (${(stockModal.data.rows || []).reduce((sum, row) => sum + (Number(row.quantityBalance) || 0), 0)})`
                      : "Available: No"}
                  </span>
                </div>
                {stockModal.data.rows.length === 0 ? (
                  <p>No stock balances found for this item.</p>
                ) : (
                  <div className="inventory-stock-table-wrap">
                    <table className="inventory-table inventory-stock-table">
                      <thead>
                        <tr>
                          <th className="inventory-stock-date-header">
                            <button
                              type="button"
                              className="inventory-stock-date-sort"
                              onClick={() => setStockDateSort((current) => current === "asc" ? "desc" : "asc")}
                              aria-label={`Sort by date transferred ${stockDateSort === "asc" ? "descending" : "ascending"}`}
                            >
                              Date Transferred <span className="sort-arrow-button" data-direction={stockDateSort === "asc" ? "asc" : "desc"} aria-hidden="true" />
                            </button>
                          </th>
                          <th>Location</th>
                          <th>Serial #</th>
                          <th>Qty</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedStockRows.map((row) => (
                          <tr key={row.id}>
                            <td>{renderTransactionTimestamp(row.transferDate)}</td>
                            <td>{row.location}</td>
                            <td>{row.serial || "-"}</td>
                            <td>{row.quantity}</td>
                            <td>{Number(row.cost || 0).toLocaleString(undefined, { style: "currency", currency: "USD" })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

    </>
  );
}
